import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect } from "react";
import {
  createSearchParams,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import isEmpty from "../../../common/helpers/isEmpty";
import {
  fetchCustomerOrgContracts,
  resetCustomerContracts,
  selectAllCustomerContracts,
} from "../customerContractsSlice";
import { resetCustomerCostCenters } from "../customerCostCentersSlice";
import { resetCustomerOrganizationState } from "../customerOrganizationSlice";
import {
  fetchCustomerOrgs,
  selectAllCustomerOrganizations,
  selectCustomerOrganizationById,
} from "../customerOrganizationsSlice";
import { resetCustomerWorkLocations } from "../customerWorkLocationsSlice";
import { CustomerOrganizationsResult } from "../types/queries";
import { TabValue } from "../types/types";
import Header from "./Header";
import TabPanelComponent from "./TabPanelComponents";
import TabsComponent from "./TabsComponent";
import FiltersList from "./filters/FiltersList";
import { CustomerManagementContainer, GreyBackground } from "./styles";

export const createFiltersFor = (
  organizations: CustomerOrganizationsResult[],
): CustomerOrganizationsResult[] => {
  // TODO: It makes no sense to have multiple representative organizations; but what if for some defective reason we do?
  const representativeOrganization = organizations.find(
    (org) => org.isRepresentative,
  );

  const restOfTheOrganizations = organizations
    .filter((org) => !org.isRepresentative)
    .sort((a, b) => a.name.localeCompare(b.name, "fi"));

  return [representativeOrganization, ...restOfTheOrganizations].filter(
    (org) => org !== undefined,
  ) as CustomerOrganizationsResult[];
};

const CustomerManagement = () => {
  const [searchParams] = useSearchParams();
  const { customerCompanyId, tab } = useParams();
  const tabValue = (tab as TabValue) || "basicInfo";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const customerCompanyOrganizations: CustomerOrganizationsResult[] =
    useAppSelector(selectAllCustomerOrganizations).filter(
      (org) => org.customerCompanyId === customerCompanyId,
    );

  const rawOrgId = searchParams.get("org");
  const selectedCustomerOrgId =
    rawOrgId && rawOrgId !== "null" ? rawOrgId : null;

  const filters = createFiltersFor(customerCompanyOrganizations);
  const representativeOrganization = filters[0];

  useEffect(() => {
    dispatch(fetchCustomerOrgs({ companyId: customerCompanyId }));

    // leave customerOrganizations out of the dependencies array
    // to fetch only once even if customerOrganizations are still empty (error case)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCompanyId, dispatch]);
  const eezyCompanyId = searchParams.get("ecom") as string;

  useEffect(() => {
    return () => {
      // Reset redux state
      dispatch(resetCustomerOrganizationState());
      dispatch(resetCustomerCostCenters());
      dispatch(resetCustomerContracts());
      dispatch(resetCustomerWorkLocations());
    };
  }, [dispatch]);

  const selectedCustomerOrg = useAppSelector((state) =>
    selectCustomerOrganizationById(state, selectedCustomerOrgId as string),
  );

  // Optional chaining guards against contracts upserted via fetchContractById,
  // which store a flat eezyCompanyId without a nested eezyCompany object.
  // TODO YLPE-8547: fix at the source in customerContractsSlice instead.
  const selectedCustomerContract = useAppSelector((state) =>
    selectAllCustomerContracts(state),
  ).find((contract) => contract.eezyCompany?.id === eezyCompanyId);

  const title =
    (selectedCustomerContract?.eezyCompany.name as string) ||
    (selectedCustomerOrg?.name as string);

  const customerContracts = useAppSelector((state) =>
    selectAllCustomerContracts(state),
  ).sort((a, b) => a.id.localeCompare(b.id));

  const getPath = (tabValue: TabValue) => {
    const pathname = `/home/customers/${customerCompanyId}/${tabValue}`;

    const cusOrgId = selectedCustomerOrgId || representativeOrganization?.id;

    const getEezyCompanyId = () => {
      // Use existing ecom param if it exists
      if (eezyCompanyId && eezyCompanyId !== "null") return eezyCompanyId;
      // Get first Eezy Company list item if it exists
      if (
        customerContracts?.length > 0 &&
        customerCompanyId === customerContracts[0].customerCompanyId
      ) {
        return customerContracts[0].eezyCompany?.id;
      }
    };

    const ecomId = getEezyCompanyId();

    switch (tabValue) {
      case "orderTemplates":
      case "contracts":
        // Navigating to a view which uses EezyCompanyFilter

        return {
          pathname,
          search: createSearchParams({
            ...(cusOrgId && { org: cusOrgId }),
            ...(ecomId && { ecom: ecomId }),
          }).toString(),
        };
      default:
        // Navigating to a view which uses CustomerOrganizationFilter
        return {
          pathname,
          search: createSearchParams({
            ...(cusOrgId && {
              org: cusOrgId,
            }),
            ...(ecomId && {
              ecom: ecomId,
            }),
          }).toString(),
        };
    }
  };

  const handleTabChange = (newTabValue: TabValue) =>
    navigate(getPath(newTabValue));

  useEffect(() => {
    const fetchContracts = async () => {
      if (selectedCustomerOrgId) {
        const contractsAction = await dispatch(
          fetchCustomerOrgContracts(selectedCustomerOrgId),
        );

        // if sub-organization's contracts are empty,
        // fetch and use contracts by representative organisation
        if (isFulfilled(contractsAction) && isEmpty(contractsAction.payload)) {
          dispatch(fetchCustomerOrgContracts(representativeOrganization?.id));
        }
      } else {
        dispatch(fetchCustomerOrgContracts(representativeOrganization?.id));
      }
    };

    fetchContracts();
  }, [dispatch, selectedCustomerOrgId, representativeOrganization?.id]);

  return (
    <CustomerManagementContainer data-testid="customer-management-page-root">
      <FiltersList
        selectedTab={tabValue}
        filters={filters}
        representativeOrganization={representativeOrganization}
      />
      <GreyBackground>
        <Header title={title} />
        <TabsComponent
          value={tabValue}
          handleTabChange={handleTabChange}
          getPath={getPath}
        />
        <TabPanelComponent tabValue={tabValue} />
      </GreyBackground>
    </CustomerManagementContainer>
  );
};

export default CustomerManagement;
